import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Grid, Ref, Segment, Sticky } from "semantic-ui-react";

import useTheme from "../../../hooks/useTheme";
import { AppContainer } from "../../UI/Container";
import { ShareIconButtons } from "../../UI/CustomElements";
import { AppColumn, AppRow } from "../../UI/Layouts";
import AppText from "../../UI/Texts/AppTexts";

import { connect, useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  fetchAndSetCurrentJob,
  fetchRelatedJobs,
} from "../../../redux/Jobs/jobsActions";
import { openModal } from "../../../redux/Modals/ModalActions";
import { useResponsive } from "../../UI/ResponsiveContext";
import { SizedBox } from "../../UI/Sizes";
import "./JobDetailPage.css";
import { MobileApplicationCTA } from "./Sections/MobileApplicationCTA";
import InlineApplicationSection from "./Sections/InlineApplicationSection";
import { JobHeader } from "./Sections/JobHeader";
import { JobInformationSection } from "./Sections/JobInformationSection";
import { RelatedJobsSection } from "./Sections/RelatedJobsSection";
import { RequirementSection } from "./Sections/RequirementSection";
import { WorkplaceSection } from "./Sections/WorkplaceSection";
import { useCrypto } from "../../UI/Encryption/CryptoProvider";
import { decryptData } from "../../../utils/cryptoFunctions";
import {
  isoDate,
  scrollToSection,
} from "../../../utils/helperFunctions";
import { JobUnavailable } from "./Sections/JobUnavailable";
import AppFooter from "../../Footers/AppFooter";
import LearnMoreAboutWorkplace from "./Components/LearnMoreAboutWorkplace";
import { t } from "i18next";
import { JobPageNotFound } from "./Sections/JobPageNotFound";
import useApplicationContact from "../../Candidate/ApplyCandidate/hooks/useApplicationContact";
import { buildJobCandidateFormPath } from "../../../utils/navigationFunctions";
import { getOrCreateApplicationSessionId } from "../../../utils/Constants/applicationStepTracking";
import { logUserAction } from "../../../utils/analyticsFunctions";
import UserActions from "../../../utils/Constants/UserActions";
import { persistApplicationCandidate } from "../../../utils/applicationStorage";
import { JobsService } from "../../../services/jobs.service";
import {
  consumeCapturedApplicationPrefillToken,
} from "../../../utils/applicationPrefill";
import {
  JOB_ENGAGEMENT_INTERACTIONS,
  captureJobEngagementToken,
  createJobEngagementTracker,
} from "../../../utils/jobEngagement";

const mapStateToProps = (state) => {
  return {
    jobs: state.jobs,
  };
};

const JobDetailPage = ({
  jobs,
  fetchAndSetCurrentJob,
  openModal,
  initialJobID,
  fromSheet = false,
}) => {
  const [searchParams] = useSearchParams();
  const {
    id,
    sharedBy: sharedByPathParam,
    sharedTenantId: sharedTenantIdPathParam,
  } = useParams();

  const auth = useSelector((state) => state.auth);
  const currentUser = auth.currentUser;
  const authProfile = auth.profile;

  const isAuthenticated = currentUser != null;

  const location = useLocation();

  const { isMobile } = useResponsive();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const applicationSectionRef = useRef(null);
  const jobEngagementTrackerRef = useRef(null);
  const requestedJobContextRef = useRef(null);
  const capturedPrefillRef = useRef(null);

  const currentPagePath = `${theme.baseUrl}${location.pathname}`;

  // Strip any /share/<uid>/<tenantId> suffix when deriving a job id from the path.
  const extractIdFromPath = location.pathname
    .replace("/jobb/", "")
    .replace(/\/share\/.+$/, "");
  const sharedByParam = sharedByPathParam ?? null;
  const sharedTenantIdParam = sharedTenantIdPathParam ?? null;

  const currentJobId = initialJobID ?? id ?? extractIdFromPath;

  const { getCryptoInfo } = useCrypto();

  const [inlineCandidateData, setInlineCandidateData] = useState(null);
  const [applicationPrefillToken, setApplicationPrefillToken] = useState(null);
  const [secureApplicationPrefill, setSecureApplicationPrefill] = useState(null);
  const [securePrefillJobId, setSecurePrefillJobId] = useState(null);
  const [securePrefillStatus, setSecurePrefillStatus] = useState("idle");
  const [jobEngagementToken] = useState(() => captureJobEngagementToken());

  const isMobileView = isMobile;

  const sectionPath = searchParams.get("section");

  const contextRef = useRef(null);
  const descriptionRef = useRef(null);
  const requirementsRef = useRef(null);
  const workplaceRef = useRef(null);
  const relatedJobsRef = useRef(null);
  const sectionRefs = useMemo(
    () => ({
      description: descriptionRef,
      requirements: requirementsRef,
      workplace: workplaceRef,
      relatedJobs: relatedJobsRef,
    }),
    [],
  );

  const dashboardJobs = jobs?.dashboardJobs ?? [];
  const allRelatedJobs = jobs?.relatedJobs ?? [];

  const currentJob = jobs?.currentJob;

  const selectedJobData =
    currentJob?.data ?? dashboardJobs.find((job) => job?.jobId === id);

  useEffect(() => {
    const jobId = selectedJobData?.jobId;
    if (!jobEngagementToken || !jobId || fromSheet) return undefined;

    const tracker = createJobEngagementTracker({
      token: jobEngagementToken,
      jobId,
    });
    if (!tracker) return undefined;
    jobEngagementTrackerRef.current = tracker;
    tracker.start();

    return () => {
      tracker.stop();
      if (jobEngagementTrackerRef.current === tracker) {
        jobEngagementTrackerRef.current = null;
      }
    };
  }, [fromSheet, jobEngagementToken, selectedJobData?.jobId]);

  useEffect(() => {
    const token = consumeCapturedApplicationPrefillToken(
      location.pathname,
    );
    if (!token) return;

    const captureKey = `${location.pathname}:${token}`;
    if (capturedPrefillRef.current === captureKey) return;
    capturedPrefillRef.current = captureKey;
    setSecureApplicationPrefill(null);
    setSecurePrefillJobId(null);
    setApplicationPrefillToken(token);
    setSecurePrefillStatus("loading");
  }, [location.pathname]);

  useEffect(() => {
    const jobId = selectedJobData?.jobId;
    if (!applicationPrefillToken || !jobId) return;

    let active = true;
    JobsService.exchangeApplicationPrefill(applicationPrefillToken, jobId)
      .then((response) => {
        if (!active) return;
        if (response.data?.status !== 0) {
          setSecurePrefillStatus("error");
          return;
        }
        setSecureApplicationPrefill(response.data.prefill ?? null);
        setSecurePrefillJobId(jobId);
        setSecurePrefillStatus("resolved");
      })
      .catch(() => {
        if (active) setSecurePrefillStatus("error");
      })
      .finally(() => {
        if (active) setApplicationPrefillToken(null);
      });

    return () => {
      active = false;
    };
  }, [applicationPrefillToken, selectedJobData?.jobId]);

  const decryptAndFetchJob = useCallback(async () => {
    let finalJobID = currentJobId;

    if (!currentJobId) {
      return;
    }

    const idParts = currentJobId.split("-id-");
    const hasPlainSlugId = idParts.length > 1 && Boolean(idParts[1]);

    if (hasPlainSlugId) {
      finalJobID = idParts[1];
    }

    // Plain job slugs can contain provider IDs such as seed_medlo_047. The
    // underscore is part of the ID, not an indication that the URL is encrypted.
    if (!hasPlainSlugId) {
      try {
        const cryptoInfo = await getCryptoInfo();
        if (cryptoInfo) {
          const splitID = currentJobId.split("_");
          if (splitID.length > 1) {
            const decryptedData = await decryptData(splitID[1], cryptoInfo);
            if (decryptedData?.jobId) {
              finalJobID = decryptedData.jobId;
            }
          }
        }
      } catch (error) {
        // Fall back to the URL-derived ID when an encrypted link can't be decrypted.
      }
    }

    await fetchAndSetCurrentJob(finalJobID, true);
  }, [currentJobId, fetchAndSetCurrentJob, getCryptoInfo]);

  useEffect(() => {
    const requestKey = currentJobId ?? "";

    if (
      !selectedJobData &&
      requestedJobContextRef.current !== requestKey
    ) {
      requestedJobContextRef.current = requestKey;
      decryptAndFetchJob();
    }

    if (selectedJobData && currentJob && !fromSheet) {
      fetchRelatedJobs(dispatch, currentJob);
    }
  }, [
    currentJob,
    currentJobId,
    decryptAndFetchJob,
    dispatch,
    fromSheet,
    selectedJobData,
  ]);

  // Extract a usable job identifier from the URL synchronously so we can
  // start the contact people fetch in parallel with the job detail fetch.
  const earlyJobId = useMemo(() => {
    if (!currentJobId) return null;
    const parts = currentJobId.split("-id-");
    if (parts.length > 1 && parts[1]) return parts[1];
    // Encrypted IDs (contain "_") can't be extracted without async decryption
    if (currentJobId.includes("_")) return null;
    return currentJobId;
  }, [currentJobId]);

  const contactPeopleJobId = selectedJobData?.jobId || earlyJobId;
  const hasActiveSecurePrefill =
    securePrefillJobId === selectedJobData?.jobId;
  const applicationContact = useApplicationContact({
    jobId: fromSheet ? null : contactPeopleJobId,
    jobData: selectedJobData,
    currentUser,
    authProfile,
    applicationEmail: hasActiveSecurePrefill ?
      secureApplicationPrefill?.email : null,
    sharedBy: sharedByParam,
    sharedTenantId: sharedTenantIdParam,
  });
  const applicationSessionId = useMemo(
    () => getOrCreateApplicationSessionId(selectedJobData?.jobId),
    [selectedJobData?.jobId],
  );

  useEffect(() => {
    if (selectedJobData && sectionPath) {
      scrollToSection(sectionRefs[sectionPath]);
    }
  }, [sectionPath, sectionRefs, selectedJobData]);

  const [isVisible, setIsVisible] = useState(false);
  const threshold = 0.05;

  useEffect(() => {
    const applicationElement = applicationSectionRef.current;
    if (!applicationElement || typeof IntersectionObserver === "undefined") {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold }
    );

    observer.observe(applicationElement);

    return () => {
      observer.unobserve(applicationElement);
    };
  }, [selectedJobData?.jobId]);

  const isJobArchieved = ["archived", "disabled", "expired"].includes(
    selectedJobData?.status
  ) || ["archived", "disabled", "expired"].includes(
    currentJob?.jobStatus
  );

  const healthcareWorkplaceData = Object.freeze(
    currentJob?.workplaceResponse ?? null
  );

  const staffingWorkplaceData = healthcareWorkplaceData?.staffingWorkplace;

  const descriptionObject = selectedJobData?.descriptionObject;
  const htmlJobDescription = descriptionObject?.value;

  const descriptionContent = descriptionObject && (
    <div
      className="content-container"
      dangerouslySetInnerHTML={{
        __html: htmlJobDescription,
      }}
    />
  );

  const handleInlineApplicationSubmitted = ({
    candidateData,
    contactPerson: submittedContactPerson,
  }) => {
    const path = buildJobCandidateFormPath(
      selectedJobData,
      submittedContactPerson ?? applicationContact.contactPerson,
      { continueApplication: true },
    );
    if (!path) return;

    navigate(path, {
      state: {
        candidateData,
        applicationSessionId,
      },
    });
  };

  const handleInlineCandidateData = (candidateData) => {
    jobEngagementTrackerRef.current?.recordInteraction(
      JOB_ENGAGEMENT_INTERACTIONS.applicationStarted,
    );
    setInlineCandidateData(candidateData);
    persistApplicationCandidate(selectedJobData?.jobId, candidateData);
  };

  const inlineApplication = selectedJobData && !fromSheet && (
    <InlineApplicationSection
      ref={applicationSectionRef}
      jobData={selectedJobData}
      auth={auth}
      candidateData={inlineCandidateData}
      contactPerson={applicationContact.contactPerson}
      contactStatus={applicationContact.status}
      ensureContactPerson={applicationContact.ensureContactPerson}
      eligibleContactPeople={applicationContact.eligibleContactPeople}
      canChangeContactPerson={applicationContact.canChangeContactPerson}
      onSelectContactPerson={applicationContact.selectContactPerson}
      applicationPrefill={
        hasActiveSecurePrefill ? secureApplicationPrefill : null
      }
      isPrefillLoading={
        securePrefillStatus === "loading"
      }
      applicationSessionId={applicationSessionId}
      onUpdateCandidateData={handleInlineCandidateData}
      onSubmitted={handleInlineApplicationSubmitted}
    />
  );

  const jobContentView = (
    <div>
      {fromSheet && (
        <style>
          {" "}
          {`
          /* Hide scrollbar for Chrome, Safari and Opera */
          *::-webkit-scrollbar {
            display: none !important;
          }

          /* Hide scrollbar for IE, Edge and Firefox */
          * {
            -ms-overflow-style: none !important;  /* IE and Edge */
            scrollbar-width: none !important;  /* Firefox */
          }

          .ui.segment,
          .ui.card,
          .ui.sticky,
          .scrolling-container {
            -ms-overflow-style: none !important;
            scrollbar-width: none !important;
          }

          .ui.segment::-webkit-scrollbar,
          .ui.card::-webkit-scrollbar,
          .ui.sticky::-webkit-scrollbar,
          .scrolling-container::-webkit-scrollbar {
            display: none !important;
          }`}
        </style>
      )}

      <AppContainer>
        <div style={{ marginTop: !fromSheet && theme.spacing.elementPadding }}>
          <Ref innerRef={contextRef}>
            <Grid>
              <Grid.Column
                mobile={16}
                tablet={fromSheet ? 16 : 8}
                computer={fromSheet ? 16 : 10}
              >
                <AppColumn
                  style={{
                    marginRight: !isMobileView && theme.spacing.contentPadding,
                  }}
                >
                  <JobHeader
                    openModal={openModal}
                    jobData={selectedJobData}
                    staffingWorkplaceData={staffingWorkplaceData}
                  />
                  <JobInformationSection jobData={selectedJobData} />
                  <RequirementSection
                    requirementsRef={requirementsRef}
                    jobData={selectedJobData}
                  />
                  <SizedBox height={theme.spacing.contentElementPadding} />

                  {(selectedJobData?.publicDescription ||
                    selectedJobData?.description?.length > 0) && (
                    <>
                      <div ref={descriptionRef} />
                      <JobDescriptionCard
                        description={
                          selectedJobData?.publicDescription ??
                          selectedJobData?.description
                        }
                        rawDescription={
                          selectedJobData?.publicDescription
                            ? null
                            : descriptionContent
                        }
                      />
                    </>
                  )}
                  {isMobileView && inlineApplication}
                  {isMobileView && (
                    <SizedBox height={theme.spacing.contentPadding} />
                  )}
                  {!isAuthenticated && (
                    <div>
                      <AppText.BodyText
                        use="h2"
                        margins={{
                          marginTop: "0px",
                          marginBottom: "0px",
                        }}
                        fontWeight={600}
                      >
                        {t("workplace")}
                      </AppText.BodyText>
                      <SizedBox height={theme.spacing.elementPadding} />
                    </div>
                  )}

                {(() => {
                  if ((isAuthenticated && healthcareWorkplaceData) || !healthcareWorkplaceData?.staffingWorkplace) {
                    return (
                      <div>
                        <WorkplaceSection
                          workplaceRef={workplaceRef}
                          workplaceData={healthcareWorkplaceData}
                          jobData={selectedJobData}
                        />
                        <SizedBox height={theme.spacing.contentPadding} />
                      </div>
                    );
                  } else if (!isAuthenticated) {
                    return (<LearnMoreAboutWorkplace openModal={openModal} jobData={selectedJobData} />);
                  }
                })()}
                <AppColumn crossAxisAlignment={"start"}>
                  {staffingWorkplaceData && <AppColumn crossAxisAlignment={'start'}>
                    <AppText.BodyText use='h2' margins={{
                      marginTop: '0px',
                      marginBottom: '0px'
                    }} fontWeight={600}>
                      {t('jobdetail.about.partner', {
                        partner: staffingWorkplaceData?.name_obj,
                      })}
                    </AppText.BodyText>
                    <SizedBox height={theme.spacing.elementPadding} />
                    <AppText.BodyText>
                      {staffingWorkplaceData?.description}
                    </AppText.BodyText>
                    <SizedBox height={theme.spacing.contentPadding} />
                  </AppColumn>}

                    <AppText.BodyText
                      use="h2"
                      margins={{
                        marginTop: "0px",
                        marginBottom: "0px",
                      }}
                      fontWeight={600}
                    >
                      {t("jobdetail.about.appName", { appName: theme.appName })}
                    </AppText.BodyText>
                    <SizedBox height={theme.spacing.elementPadding} />
                    <AppText.BodyText>
                      {theme.description || t("about_appName_long", { appName: theme.appName })}
                    </AppText.BodyText>
                    <SizedBox height={theme.spacing.contentPadding} />
                  </AppColumn>
                  <SizedBox height={theme.spacing.elementPadding} />
                  <AppRow mainAxisAlignment="end">
                    <AppColumn crossAxisAlignment={"end"}>
                      <AppText.FootnoteSmall color={theme.colors.grey}>
                        {t("jobdetail.published")}
                      </AppText.FootnoteSmall>
                      <SizedBox height={"0.25rem"} />
                      <AppText.Caption>
                        {isoDate({
                          dateString: selectedJobData?.createdAt,
                          removeTime: true,
                        })}
                      </AppText.Caption>
                    </AppColumn>
                  </AppRow>
                </AppColumn>
              </Grid.Column>
              {!isMobileView && !fromSheet && (
                <Grid.Column mobile={16} tablet={8} computer={6}>
                  <AppColumn
                    style={{
                      marginLeft: theme.spacing.contentPadding,
                    }}
                  >
                    <AppRow mainAxisAlignment="end">
                      <ShareIconButtons
                        link={currentPagePath}
                        type="jobb"
                        id={selectedJobData?.jobId}
                      />
                    </AppRow>
                    <SizedBox
                      height={theme.spacing.jobApplicationSocialGap}
                    />
                    <Sticky
                      context={contextRef}
                      offset={theme.sizing.jobApplicationStickyTopOffset}
                    >
                      {inlineApplication}
                      <SizedBox height={theme.spacing.contentPadding} />
                    </Sticky>
                  </AppColumn>
                </Grid.Column>
              )}
            </Grid>
          </Ref>
          <SizedBox height={theme.spacing.contentPaddingBig} />
        </div>
      </AppContainer>
      {!fromSheet && !isVisible && isMobileView && (
        <MobileApplicationCTA
          jobData={selectedJobData}
          onClick={() => {
            jobEngagementTrackerRef.current?.recordInteraction(
              JOB_ENGAGEMENT_INTERACTIONS.applicationStarted,
            );
            logUserAction(UserActions.application_jump_clicked, {
              jobId: selectedJobData?.jobId,
              analyticsPayload: {
                application_session_id: applicationSessionId,
                application_source: "merged_job_page",
              },
            });
            scrollToSection(applicationSectionRef);
            window.setTimeout(() => {
              const firstInput = applicationSectionRef.current?.querySelector(
                "input:not([disabled])",
              );
              firstInput?.focus();
            }, 350);
          }}
          className="mobile-application-cta"
          style={{
            position: "fixed",
            backgroundColor: theme.colors.white,
            color: theme.primaryColor,
            zIndex: 1000,
          }}
        />
      )}
    </div>
  );

  let isLoading = !selectedJobData;
  const jobNotFound = currentJob?.notFound ?? false;

  return (
    <div
      style={{
        minHeight: "90vh",
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        minWidth: "100vw",
      }}
    >
      {(() => {
        if (isJobArchieved) {
          return (
            <AppContainer>
              <JobUnavailable
                job={currentJob?.data ?? selectedJobData}
                hasSimilarJobs={allRelatedJobs.length > 0}
              />
            </AppContainer>
          );
        } else if (jobNotFound) {
          return (
            <AppContainer style={{ height: "100vh" }}>
              <JobPageNotFound />
            </AppContainer>
          );
        } else if (isLoading) {
          return (
            <Segment
              basic
              loading={isLoading}
              placeholder={isLoading}
              style={isLoading ? { height: "80vh" } : {}}
            />
          );
        } else {
          return jobContentView;
        }
      })()}
      {allRelatedJobs.length > 0 && (
        <div ref={relatedJobsRef}>
          <RelatedJobsSection allRelatedJobs={allRelatedJobs} />
        </div>
      )}
      <SizedBox height={theme.spacing.contentPadding} />
      <AppFooter forceShow={true} />
    </div>
  );
};

const JobDescriptionCard = ({ description, rawDescription }) => {
  const theme = useTheme();
  return (
    <AppColumn>
      <AppText.BodyText
        use="h2"
        margins={{
          marginTop: "0px",
          marginBottom: "0px",
        }}
        fontWeight={600}
      >
        {t("jobdetail.description.title")}
      </AppText.BodyText>
      <SizedBox height={theme.spacing.elementPadding} />
      {rawDescription ?? <AppText.BodyText>{description}</AppText.BodyText>}
      <SizedBox height={theme.spacing.contentPadding} />
    </AppColumn>
  );
};

export default connect(mapStateToProps, {
  fetchAndSetCurrentJob,
  openModal,
})(JobDetailPage);
